import { EventEmitter, Injectable, Output } from '@angular/core';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable } from 'rxjs';
import { Config } from 'src/app/app-constant';
import { GetCompanyAndUsersData } from 'src/app/services/get-company-and-users-data/get-company-and-users-data.service';
import { HttpService } from 'src/app/service/http/http.service';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import { Capacitor } from '@capacitor/core';

export interface TableState<T = any> {
  originalData: T[];
  filteredData: T[];
  pageSize: number;
  currentPage: number;
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc' | '';
}

@Injectable({
  providedIn: 'root'
})
export class DwmGenericServiceService {
  private tabLabelSource = new BehaviorSubject<string | null>(null);
  showMtdView: any = new BehaviorSubject<any>(null);
  tabLabel$ = this.tabLabelSource.asObservable();

  monthlyFrequencyList: string[] = ['HOURLY', 'DAILY', 'WEEKLY', 'FORTNIGHTLY'];
  monthlyFrequencyListL4Above: string[] = ['DAILY', 'WEEKLY', 'FORTNIGHTLY'];
  annualFrequencyList: string[] = ['MONTHLY', 'QUARTERLY', 'SEMI-ANNUALLY', 'ANNUALLY'];
  checkDwmStatus = ['Not_Started', 'In_Progress', 'Completed', 'Done_with_delay'];
  checkDwmCriticality = ['Critical', 'Non-Critical'];
  statusLabelAndValue = [
    {
      value: 'Not_Started',
      label: 'Not Completed'
    },
    {
      value: 'In_Progress',
      label: 'In Progress'
    },
    {
      value: 'Completed',
      label: 'Done On Time'
    },
    {
      value: 'Done_with_delay',
      label: 'Done with Delay'
    },
    {
      value: 'cutOffDate',
      label: 'Cut-off Date '
    },
    {
      value: 'rndAdmin',
      label: 'R&D Admin'
    },
    {
      value: 'monthlyDwm',
      label: 'Monthly DWM'
    },
    {
      value: 'yearlyDwm',
      label: 'Annually DWM'
    },
    {
      value: 'categoryName',
      label: 'Category Name'
    },
    {
      value: 'userIds',
      label: 'UserID'
    },
    {
      value: 'grades',
      label: 'Grades'
    },
    {
      value: 'productName',
      label: 'Product Name'
    }
  ];

  dwmCutOffDate: number | null = null;
  dwmCutOffDateInMs: number | null = null;
  getAllAccessAdmins: any = null;

  constructor(
    private toastrService: ToastrService,
    private config: Config,
    private gcetCompanyAndUsersData: GetCompanyAndUsersData,
    private httpService: HttpService
  ) {
    if (this.checkIsLoggedInUserIsAdmin().userIsAdmin) {
      this.getAllAccessAdmins = this.getAllAccessMatrix(this.gcetCompanyAndUsersData.loggedInUserCompanyID);
    }

    this.getEmployeeList();
  }

  /*
  Download table in EXCEL
  */
  async saveAsExcelFile(data: any, workSheetName: string, fileName: string) {
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
    const workbook: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, workSheetName);

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    });

    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const formattedDate = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
    const finalName = `${fileName}-${formattedDate}.xlsx`;

    // 🌐 WEB
    if (Capacitor.getPlatform() === 'web') {
      saveAs(blob, finalName);
      return;
    }

    // 📱 MOBILE
    const base64 = await this.blobToBase64(blob);

    const saved = await Filesystem.writeFile({
      path: finalName,
      data: base64,
      directory: Directory.Documents
    });

    await this.openFile(saved, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  }

  /*
  Download table in PDF
  Requirements
  Table Headers: Array of Data eg: [data]
  Table Data: Array of Data eg: [data]
  */
  async downloadTableAsPdf(tableHeaders: string[], tableData: any[], tableTitle: string, tableName: string) {
    const leftLogo = 'assets/icon/logo_advik.png';
    const rightLogo = 'assets/icon/Phiedge-logo_HRMS_01.png';

    const formattedDate = new Date()
      .toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
      .replace(/\//g, '_');

    const doc = new jsPDF('l', 'mm', 'a3');
    const pageWidth = doc.internal.pageSize.getWidth();

    const fixedLogoWidth = 30;
    const marginX = 10;
    const logoY = 10;

    /* ---------- LEFT LOGO ---------- */
    const leftImgProps = doc.getImageProperties(leftLogo);
    const leftLogoHeight = (leftImgProps.height * fixedLogoWidth) / leftImgProps.width;

    doc.addImage(leftLogo, 'PNG', marginX, logoY, fixedLogoWidth, leftLogoHeight);

    /* ---------- RIGHT LOGO ---------- */
    const rightImgProps = doc.getImageProperties(rightLogo);
    const rightLogoHeight = (rightImgProps.height * fixedLogoWidth) / rightImgProps.width;

    doc.addImage(rightLogo, 'PNG', pageWidth - fixedLogoWidth - marginX, logoY, fixedLogoWidth, rightLogoHeight);

    /* ---------- TITLE ---------- */
    const titleY = logoY + Math.max(leftLogoHeight, rightLogoHeight) + 8;

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(tableTitle, pageWidth / 2, titleY, { align: 'center' });

    /* ---------- COLUMN WIDTH LOGIC ---------- */

    // Find which columns actually have data
    const columnHasData: boolean[] = tableHeaders.map((_, colIndex) => tableData.some(row => row[colIndex] && row[colIndex].toString().trim() !== ''));

    // Build column styles dynamically
    const emptyColumns = this.getEmptyColumnIndexes(tableHeaders, tableData);

    const columnStyles: any = {
      3: { cellWidth: 50 } // Actionable column wider (your existing rule)
    };

    emptyColumns.forEach(index => {
      columnStyles[index] = {
        cellWidth: 'wrap',
        overflow: 'linebreak'
      };
    });

    // Make Actionable column wider if present
    const actionableIndex = tableHeaders.findIndex(h => h.toLowerCase().includes('actionable'));
    if (actionableIndex !== -1) {
      columnStyles[actionableIndex] = { cellWidth: 55 };
    }

    /* ---------- TABLE ---------- */

    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
      startY: titleY + 8,
      theme: 'grid',

      styles: {
        fontSize: 9,
        cellPadding: 3,
        valign: 'middle'
      },

      headStyles: {
        fillColor: [230, 230, 230],
        textColor: 0,
        fontStyle: 'bold',
        halign: 'center',
        overflow: 'linebreak'
      },

      columnStyles
    });

    // doc.save(`${tableName}_${formattedDate}.pdf`);
    const pdfName = `${tableName}_${formattedDate}.pdf`;

    if (Capacitor.getPlatform() === 'web') {
      doc.save(pdfName);
    } else {
      const pdfBase64 = doc.output('datauristring').split(',')[1];

      const savedFile = await Filesystem.writeFile({
        path: pdfName,
        data: pdfBase64,
        directory: Directory.Documents
      });

      await this.openFile(savedFile, 'application/pdf');
    }
  }

  async downloadTableAsPdfReport(tableHeaders: string[], tableData: any[], tableTitle: string, tableName: string, tableOptions?: any) {
    const leftLogo = 'assets/icon/logo_advik.png';
    const rightLogo = 'assets/icon/Phiedge-logo_HRMS_01.png';

    const formattedDate = new Date()
      .toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
      .replace(/\//g, '_');

    /* ===================== PAGE SIZE (KEY FIX) ===================== */
    // Custom wide page to fit 31 days
    const customWidth = 500;
    const customHeight = 210;

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: [customWidth, customHeight]
    });

    const pageWidth = customWidth;
    const marginX = 10;

    /* ===================== LOGOS ===================== */
    const fixedLogoWidth = 30;
    const logoY = 10;

    // LEFT LOGO
    const leftImgProps = doc.getImageProperties(leftLogo);
    const leftLogoHeight = (leftImgProps.height * fixedLogoWidth) / leftImgProps.width;

    doc.addImage(leftLogo, 'PNG', marginX, logoY, fixedLogoWidth, leftLogoHeight);

    // RIGHT LOGO
    const rightImgProps = doc.getImageProperties(rightLogo);
    const rightLogoHeight = (rightImgProps.height * fixedLogoWidth) / rightImgProps.width;

    doc.addImage(rightLogo, 'PNG', pageWidth - fixedLogoWidth - marginX, logoY, fixedLogoWidth, rightLogoHeight);

    /* ===================== TITLE ===================== */
    const titleY = logoY + Math.max(leftLogoHeight, rightLogoHeight) + 8;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(tableTitle, pageWidth / 2, titleY, { align: 'center' });

    /* ===================== COLUMN WIDTH CALC ===================== */
    const usableWidth = pageWidth - marginX * 2;

    // Fixed columns
    const categoryWidth = 28;
    const actionableWidth = 45;
    const frequencyWidth = 20;

    const fixedColsWidth = categoryWidth + actionableWidth + frequencyWidth;

    const dayColumnCount = tableHeaders.length - 3;

    //  NO Math.max here – we WANT them to shrink
    const dayColWidth = (usableWidth - fixedColsWidth) / dayColumnCount;

    /* ===================== COLUMN STYLES ===================== */
    const columnStyles: any = {
      0: { cellWidth: categoryWidth },
      1: { cellWidth: actionableWidth },
      2: { cellWidth: frequencyWidth }
    };

    for (let i = 3; i < tableHeaders.length; i++) {
      columnStyles[i] = {
        cellWidth: dayColWidth,
        overflow: 'linebreak',
        halign: 'center',
        valign: 'top'
      };
    }

    /* ===================== AUTOTABLE ===================== */
    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
      startY: titleY + 8,    
      theme: 'grid',
      styles: {
        fontSize: 7,
        cellPadding: 1.5,
        valign: 'top',
        overflow: 'linebreak'
      },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle',
        fontSize: 8
      },
      columnStyles,
      ...(tableOptions || {})
    });

    /* ===================== SAVE ===================== */
    // doc.save(`${tableName}_${formattedDate}.pdf`);
    const pdfName = `${tableName}_${formattedDate}.pdf`;

    if (Capacitor.getPlatform() === 'web') {
      doc.save(pdfName);
    } else {
      const pdfBase64 = doc.output('datauristring').split(',')[1];

      const savedFile = await Filesystem.writeFile({
        path: pdfName,
        data: pdfBase64,
        directory: Directory.Documents
      });

      await this.openFile(savedFile, 'application/pdf');
    }
  }

  blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.readAsDataURL(blob);
    });
  }

  async openFile(saved: any, mimeType: string) {
    const platform = Capacitor.getPlatform();

    // 🌐 WEB → just download
    if (platform === 'web') {
      window.open(saved.uri, '_blank');
      return;
    }

    // 📱 ANDROID / IOS
    const filePath = platform === 'ios' ? saved.uri.replace('file://', '') : saved.uri;

    await FileOpener.open({
      filePath,
      contentType: mimeType
    });
  }

  getEmptyColumnIndexes(headers: string[], data: any[][]): number[] {
    const emptyCols: number[] = [];

    headers.forEach((_, colIndex) => {
      const hasData = data.some(row => {
        const value = row[colIndex];
        return value !== null && value !== undefined && value !== '';
      });

      if (!hasData) {
        emptyCols.push(colIndex);
      }
    });

    return emptyCols;
  }

  sortByIdDesc(data: any[]): any[] {
    return [...data].sort((a, b) => b.id - a.id);
  }

  /* Start of Pagenation and Filters */

  init<T>(data: T[], pageSize = 25): TableState<T> {
    return {
      originalData: structuredClone(data),
      filteredData: structuredClone(data),
      pageSize,
      currentPage: 1,
      sortColumn: null,
      sortDirection: ''
    };
  }

  applyFilter<T>(state: TableState<T>, searchText: string): TableState<T> {
    const search = searchText.trim().toLowerCase();
    const filteredData = [...state.originalData].filter(row => Object.values(row as any).some(val => val?.toString().toLowerCase().includes(search)));

    return { ...state, filteredData, currentPage: 1 };
  }

  // SORT HANDLER
  sort<T>(state: TableState<T>, column: keyof T & string): TableState<T> {
    const direction = state.sortColumn === column && state.sortDirection === 'asc' ? 'desc' : 'asc';

    const sortedData = [...state.filteredData].sort((a, b) => {
      const valA = a[column];
      const valB = b[column];

      if (valA == null) return 1;
      if (valB == null) return -1;

      const result =
        typeof valA === 'number' && typeof valB === 'number'
          ? valA - valB
          : valA.toString().localeCompare(valB.toString(), undefined, {
              numeric: true,
              sensitivity: 'base'
            });

      return direction === 'asc' ? result : -result;
    });

    return {
      ...state,
      sortColumn: column,
      sortDirection: direction,
      filteredData: sortedData
    };
  }

  paginate<T>(state: TableState<T>): T[] {
    const start = (state.currentPage - 1) * state.pageSize;
    return state.filteredData.slice(start, start + state.pageSize);
  }

  changePage<T>(state: TableState<T>, page: number): TableState<T> {
    if (page < 1 || page > this.totalPages(state)) return state;
    return { ...state, currentPage: page };
  }

  changePageSize<T>(state: TableState<T>, size: number): TableState<T> {
    return { ...state, pageSize: size, currentPage: 1 };
  }

  totalEntries<T>(state: TableState<T>): number {
    return state.filteredData.length;
  }

  totalPages<T>(state: TableState<T>): number {
    return Math.ceil(this.totalEntries(state) / state.pageSize);
  }

  startEntry<T>(state: TableState<T>): number {
    return this.totalEntries(state) === 0 ? 0 : (state.currentPage - 1) * state.pageSize + 1;
  }

  endEntry<T>(state: TableState<T>): number {
    const end = state.currentPage * state.pageSize;
    return Math.min(end, this.totalEntries(state));
  }

  /* End Of Filters and pagenation */

  // Set Frequency
  getFrequency(dwmType: string, isL4Above = false) {
    return dwmType == 'Annually' ? this.annualFrequencyList : isL4Above ? [...this.monthlyFrequencyListL4Above, ...this.annualFrequencyList] : [...this.monthlyFrequencyList, ...this.annualFrequencyList];
  }
  getFrequencyAll(dwmType: string) {
    return ['HOURLY', 'DAILY', 'WEEKLY', 'FORTNIGHTLY', 'MONTHLY', 'QUARTERLY', 'SEMI_ANNUALLY', 'ANNUALLY'];
  }

  // Set Dwm Type
  getDwmType(isL4Above: string) {
    return isL4Above ? ['Annually', 'Monthly'] : ['Monthly'];
  }
  // Other Logics
  checkMaxLength(text: string) {
    if (text?.length === 500 && !this.toastrService) this.tosterErrorMessage('Maximum limit is 500 characters', 'Limit Reached');
  }

  tosterErrorMessage(message: string, title = '') {
    this.toastrService.error(message, title, {
      timeOut: 2000
    });
  }

  formatDate(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
  }

  generateUniqueIdByExistingList(list: any[]): string {
    let id: any = null;
    do {
      id = 'new_' + Date.now() + Math.floor(Math.random() * 10000);
    } while (list.some(item => item.id === id));
    return id;
  }

  getDWMSetup() {
    return this.httpService.callGetService(`${this.config.getDwmSetup}?companyId=${this.gcetCompanyAndUsersData.loggedInUserCompanyID}`);
  }

  checkGetDWMSetup(res: any) {
    const setup = res?.data?.[0];
    // console.log(setup);
    if (setup && setup.cutOffDate) {
      const now = new Date();
      const getCurrentCuttOffDate = new Date(now.getFullYear(), now.getMonth(), Number(setup.cutOffDate));
      const getCurrentCuttOffDateInMs = new Date(now.getFullYear(), now.getMonth(), Number(setup.cutOffDate)).getTime();
      return { getCurrentCuttOffDate, getCurrentCuttOffDateInMs };
    } else {
      return null;
    }
  }

  // Checks for cuttoff date according to current date
  isCreateTaskEnabled(rowDate: string | Date | null | undefined, cutoffDate: string | Date | null | undefined): boolean {
    if (!rowDate) return false;

    const row = new Date(typeof rowDate === 'string' ? rowDate.trim() : rowDate);
    if (isNaN(row.getTime())) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const rowMonthStart = new Date(row.getFullYear(), row.getMonth(), 1);

    if (rowMonthStart >= currentMonthStart) return true;

    if (cutoffDate) {
      const cutoff = new Date(cutoffDate);
      cutoff.setHours(0, 0, 0, 0);
      return today <= cutoff;
    }

    return false;
  }

  // changeTabByLabel(label: string) {
  //   this.tabLabelSource.next(label);
  // }

  checkIsLoggedInUserIsAdmin() {
    const loggedInUserRole = this.gcetCompanyAndUsersData.loggedInUserRole;
    return {
      userIsAdminType: loggedInUserRole == 'HR Admin' || loggedInUserRole == 'Super Admin' || loggedInUserRole == 'PHI Admin' || loggedInUserRole == 'Admin' || false,
      userIsSuperHrAndAdmin: loggedInUserRole == 'Super Admin' || loggedInUserRole == 'HR Admin' || loggedInUserRole == 'Admin' || false,
      userIsHrAdminAndAdmin: loggedInUserRole == 'HR Admin' || loggedInUserRole == 'Admin' || false,
      userIsAdmin: loggedInUserRole == 'Admin' || false
    };
  }

  getAllAccessMatrix(loggedInUserCompanyID: any): Observable<any> {
    // Logged in Admin is fetch automatically by token
    const request = { companyId: loggedInUserCompanyID, search: null, loggedInUserRole: 'Admin' };
    return this.httpService.callPostService(this.config.searchAccess + '?sort=id' + ',asc', request);
  }

  checkIfIsExitedSelectedDate(selectedDate: any) {
    return new Date(new Date().setHours(0, 0, 0, 0)) > new Date(new Date(selectedDate).setHours(0, 0, 0, 0));
  }

  getStatusLabel(status: string | null | undefined): string {
    if (!status) return '';
    return this.statusLabelAndValue.find(item => item.value === status)?.label || '';
  }

  // check if provided is current or previous month
  isCurrentOrPreviousMonth(input: string | Date | null | undefined): boolean {
    if (!input) return false;

    const today = new Date();
    const currentMonth = today.getMonth(); // 0-11
    const currentYear = today.getFullYear();

    let inputMonth: number;
    let inputYear: number;

    /* ---------- Date object ---------- */
    if (input instanceof Date) {
      inputMonth = input.getMonth();
      inputYear = input.getFullYear();

      /* ---------- Month name (December, Jan, etc.) ---------- */
    } else if (isNaN(Date.parse(input))) {
      const parsedMonth = new Date(`${input} 1, ${currentYear}`);
      if (isNaN(parsedMonth.getTime())) return false;

      inputMonth = parsedMonth.getMonth();

      // 🔑 CRITICAL FIX
      inputYear = inputMonth > currentMonth ? currentYear - 1 : currentYear;

      /* ---------- Full date string ---------- */
    } else {
      const parsedDate = new Date(input);
      inputMonth = parsedDate.getMonth();
      inputYear = parsedDate.getFullYear();
    }

    /* ---------- Previous month ---------- */
    const prevMonthDate = new Date(currentYear, currentMonth - 1, 1);
    const prevMonth = prevMonthDate.getMonth();
    const prevYear = prevMonthDate.getFullYear();

    return (inputMonth === currentMonth && inputYear === currentYear) || (inputMonth === prevMonth && inputYear === prevYear);
  }

  getTodaysDateFromSelectedMonth(selectedMonth: string | number | Date, financialYear?: string): Date | null {
    if (!selectedMonth) return null;

    const today = new Date();
    const day = today.getDate();

    let monthIndex: number;
    let startYear: number;
    let endYear: number;

    /* ---------------- Date input ---------------- */
    if (selectedMonth instanceof Date) {
      return new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), day);
    }

    /* ---------------- Month index ---------------- */
    if (typeof selectedMonth === 'number') {
      monthIndex = selectedMonth - 1;
    } else {
      const parsed = new Date(`${selectedMonth} 1`);
      if (isNaN(parsed.getTime())) return null;
      monthIndex = parsed.getMonth();
    }

    /* ---------------- Financial year ---------------- */
    if (financialYear) {
      const [fyStart, fyEnd] = financialYear.split('-').map(Number);
      startYear = fyStart;
      endYear = fyEnd;
    } else {
      // auto-detect CURRENT FY
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();

      if (currentMonth >= 3) {
        startYear = currentYear;
        endYear = currentYear + 1;
      } else {
        startYear = currentYear - 1;
        endYear = currentYear;
      }
    }

    /* ---------------- Assign year from FY ---------------- */
    // Apr–Dec → FY start year
    // Jan–Mar → FY end year
    const year = monthIndex >= 3 ? startYear : endYear;

    return new Date(year, monthIndex, day);
  }

  getEmployeeList() {
    this.gcetCompanyAndUsersData.getAllEmployeeByCompanyID().subscribe((res: any) => {
      // console.log(res);
    });
  }

  // Mon Jan 26 2026 22:07:26 GMT+0530  --- OP: January
  getMonthName(date: Date): string {
    return date.toLocaleString('en-IN', { month: 'long' });
  }

  // If 1/1/2026 result 0+1 = 1
  getCurrentMonthByDate(setDate: any) {
    const date = new Date(setDate);
    return date.getMonth() + 1;
  }
}
